import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ILogContent, useLogListInfiniteQuery } from 'api';
import { Form } from 'components';
import { dateFormatter } from 'constants/index';
import { Log } from 'models/Log';

export interface IFilters {
  startDate: string | null;
  endDate: string | null;
  updatedBy: string | null;
}

const initialFilters: IFilters = {
  startDate: null,
  endDate: null,
  updatedBy: null,
};

const mergePages = (pages: ILogContent[]): Record<string, Log[]> => {
  return pages.reduce(
    (acc, page) => {
      const logs = page.content?.[0].logs as Record<string, Log[]> | undefined;
      if (logs) {
        Object.entries(logs).forEach(([key, value]) => {
          if (acc[key]) {
            acc[key] = acc[key].concat(value);
          } else {
            acc[key] = value;
          }
        });
      }
      return acc;
    },
    {} as Record<string, Log[]>
  );
};

export const useLogs = ({
  entityName,
  id,
  isAdminLogs,
  service,
}: {
  entityName: string;
  id: string | undefined;
  isAdminLogs?: boolean;
  service?: string;
}) => {
  const [form] = Form.useForm();
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<IFilters>(initialFilters);
  const observerLastRef = useRef<IntersectionObserver | null>(null);
  const observerFirstRef = useRef<IntersectionObserver | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    isLoading: logListLoading,
    refetch,
  } = useLogListInfiniteQuery({
    size: 15,
    id: id as string,
    entityName,
    filters,
    isAdminLogs,
    service,
  });
  const logs = useMemo(() => {
    return mergePages(data?.pages || []);
  }, [data]);

  const lastLogRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingNextPage) return;
      if (observerLastRef.current) observerLastRef.current.disconnect();
      observerLastRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );
      observerLastRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const firstLogRef = useCallback(
    (node: any) => {
      if (!node || !scrollContainerRef.current) return;
      if (isFetchingPreviousPage || !hasPreviousPage) return;

      if (observerFirstRef.current) observerFirstRef.current.disconnect();

      observerFirstRef.current = new IntersectionObserver(
        async ([entry]) => {
          if (entry.isIntersecting && !isFetchingPreviousPage) {
            const container = scrollContainerRef.current!;
            await fetchPreviousPage();
            // After content is added, maintain scroll position
            requestAnimationFrame(() => {
              container.scrollTop = 1000;
            });
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '0px',
          threshold: 0,
        }
      );

      observerFirstRef.current.observe(node);
    },
    [isFetchingPreviousPage, hasPreviousPage, fetchPreviousPage]
  );

  const showDrawer = useCallback(() => setFilterDrawerVisible(true), []);
  const closeDrawer = useCallback(() => setFilterDrawerVisible(false), []);
  const applyFilters = useCallback(
    (values: any) => {
      const _filters: any = {};
      if (values.updatedBy) {
        _filters.updatedBy = values.updatedBy;
      }
      if (values?.dateRange?.[0]) {
        _filters.startDate = dateFormatter(values?.dateRange[0], 'start');
      }
      if (values?.dateRange?.[1]) {
        _filters.endDate = dateFormatter(values.dateRange[1], 'end');
      }
      setFilters(_filters);
      closeDrawer();
    },
    [closeDrawer]
  );

  const clearFilters = useCallback(async () => {
    form?.resetFields();
    setFilters(initialFilters);
  }, [form]);

  useEffect(() => {
    if (entityName) {
      clearFilters();
    }
  }, [entityName, clearFilters]);

  useEffect(() => {
    refetch();
  }, [filters, refetch, isAdminLogs]);

  return {
    logs,
    showDrawer,
    filterDrawerVisible,
    form,
    applyFilters,
    clearFilters,
    closeDrawer,
    logListLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    lastLogRef,
    firstLogRef,
    scrollContainerRef,
  };
};
